import { ArrowRight } from "lucide-react"

import { Button } from "@/components/ui/button"
import { t } from "@/lib/i18n"

import { LandingSection, SectionRule } from "./section-rule"
import type { LandingLinks } from "./types"

export function PricingSection({ links }: { links: LandingLinks }) {
  return (
    <LandingSection id="pricing">
      <div className="rounded-xl border border-brand/25 bg-card p-6 sm:p-10 lg:p-14">
        <SectionRule>{t("ui.landing.pricing.label")}</SectionRule>

        <div className="mt-5 flex flex-col items-start justify-between gap-6 lg:flex-row lg:items-end lg:gap-16">
          <h2 className="rotulo max-w-[13em] text-balance text-3xl sm:text-4xl lg:text-5xl">
            {t("ui.landing.pricing.title")}
          </h2>
          <p className="max-w-2xl pb-1 text-sm leading-7 text-muted-foreground lg:text-base">
            {t("ui.landing.pricing.description")}
          </p>
        </div>

        <div className="mt-10 grid gap-5 sm:grid-cols-2">
          <div className="rounded-lg border border-border bg-background p-6 sm:p-7">
            <span className="text-sm font-semibold text-muted-foreground">{t("ui.landing.pricing.monthly.label")}</span>
            <div className="mt-4 flex items-baseline gap-2.5">
              <span className="text-4xl font-bold leading-none tracking-tight tabular-nums lg:text-5xl">
                {t("ui.landing.pricing.monthly.amount")}
              </span>
              <span className="text-sm font-medium text-muted-foreground">{t("ui.landing.pricing.monthly.unit")}</span>
            </div>
            <p className="mt-3 text-sm leading-6 text-muted-foreground">{t("ui.landing.pricing.monthly.note")}</p>
          </div>

          <div className="rounded-lg border border-brand/40 bg-brand-subtle p-6 sm:p-7">
            <div className="flex items-center justify-between gap-3">
              <span className="text-sm font-semibold text-muted-foreground">{t("ui.landing.pricing.annual.label")}</span>
              <span className="inline-flex items-center rounded-full bg-brand-solid px-2.5 py-0.5 text-xs font-semibold text-white">
                {t("ui.landing.pricing.annual.badge")}
              </span>
            </div>
            <div className="mt-4 flex items-baseline gap-2.5">
              <span className="text-4xl font-bold leading-none tracking-tight tabular-nums lg:text-5xl">
                {t("ui.landing.pricing.annual.amount")}
              </span>
              <span className="text-sm font-medium text-muted-foreground">{t("ui.landing.pricing.annual.unit")}</span>
            </div>
            <p className="mt-3 text-sm leading-6 text-muted-foreground">
              {t("ui.landing.pricing.annual.note_before")}
              <span className="tabular-nums">{t("ui.landing.pricing.annual.note_amount")}</span>
              {t("ui.landing.pricing.annual.note_after")}
            </p>
          </div>
        </div>

        <div className="mt-8 flex flex-col items-start justify-between gap-6 border-t border-border pt-6 lg:flex-row lg:gap-12">
          <ul className="flex max-w-3xl flex-col gap-2.5">
            <li className="flex gap-2.5 text-sm leading-6 text-muted-foreground">
              <span className="mt-2 size-1 shrink-0 rounded-full bg-brand" aria-hidden />
              <span>
                {t("ui.landing.pricing.notes.commission_before")}
                <span className="tabular-nums">{t("ui.landing.pricing.notes.commission_online")}</span>
                {t("ui.landing.pricing.notes.commission_middle")}
                <span className="tabular-nums">{t("ui.landing.pricing.notes.commission_physical")}</span>
                {t("ui.landing.pricing.notes.commission_after")}
              </span>
            </li>
            {["invoice", "suspension"].map((key) => (
              <li key={key} className="flex gap-2.5 text-sm leading-6 text-muted-foreground">
                <span className="mt-2 size-1 shrink-0 rounded-full bg-brand" aria-hidden />
                <span>{t(`ui.landing.pricing.notes.${key}`)}</span>
              </li>
            ))}
          </ul>

          <Button asChild size="lg" className="shrink-0">
            <a href={links.staffRegistration}>
              {t("ui.landing.pricing.cta")}
              <ArrowRight />
            </a>
          </Button>
        </div>
      </div>
    </LandingSection>
  )
}
